import React, { useState, useEffect } from 'react';
import { Coffee, X, ExternalLink } from 'lucide-react';
import { supabase } from '../utils/supabase';

interface DonationOptionsProps {
  onClose?: () => void;
}

interface DonationLink {
  id: string;
  name: string;
  url: string;
  description: string;
}

export function DonationOptions({ onClose }: DonationOptionsProps) {
  const [donationLinks, setDonationLinks] = useState<DonationLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDonationLinks = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('donation_links')
          .select('*')
          .order('name');

        if (error) throw error;

        setDonationLinks(data || []);
      } catch (error) {
        console.error('Error fetching donation links:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDonationLinks();
  }, []);

  // Fallback donation links if none are configured in the database
  const fallbackLinks = [
    {
      id: 'paypal',
      name: 'PayPal',
      url: 'https://paypal.me/learndutch',
      description: 'Support this project with a one-time donation'
    },
    {
      id: 'ideal',
      name: 'iDEAL',
      url: 'https://ideal.me/learndutch',
      description: 'Support with iDEAL payment (Netherlands)'
    }
  ];

  const links = donationLinks.length > 0 ? donationLinks : fallbackLinks;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center">
            <Coffee className="h-6 w-6 text-amber-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Buy me a coffee</h2>
          </div>
          {onClose && (
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <p className="text-gray-600 mb-6">
          If you're enjoying this free Dutch learning app and would like to support its development, please consider buying me a coffee!
        </p>

        {loading ? (
          <div className="space-y-3 animate-pulse">
            <div className="h-12 bg-gray-200 rounded-lg"></div>
            <div className="h-12 bg-gray-200 rounded-lg"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {links.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 border border-gray-200 rounded-lg hover:border-amber-500 hover:shadow-md transition-all flex items-center justify-between bg-white"
              >
                <div>
                  <h3 className="font-medium text-gray-900">{link.name}</h3>
                  <p className="text-sm text-gray-500">{link.description}</p>
                </div>
                <ExternalLink className="h-5 w-5 text-amber-600" />
              </a>
            ))}
          </div>
        )}

        <div className="mt-8 text-center text-sm text-gray-500">
          Thank you for your support! It helps keep this app free for everyone.
        </div>
      </div>
    </div>
  );
}